<template>
  <div class="manage-registration-page">
    <div class="container mt-4" v-if="registrationLoaded">
      <h2 class="mb-3">Votre inscription</h2>

      <b-alert v-if="error" variant="danger" show class="mb-3">
        {{ error }}
      </b-alert>

      <b-card>
        <p><strong>Ouverture :</strong> {{ registration.title || "-" }}</p>
        <p><strong>Date :</strong> {{ formatDate(registration.date) }}</p>

        <b-form @submit.prevent="submitUpdate">
          <h5 class="mt-4">Materiel</h5>
          <b-form-checkbox v-model="form.harnais">Besoin d'un harnais</b-form-checkbox>
          <b-form-checkbox v-model="form.combi">Besoin d'une combinaison</b-form-checkbox>

          <GearSearchSelect
            v-model="form.sail"
            type="sails"
            label="Voile"
            placeholder="Rechercher une voile"
            empty-hint="Tapez pour rechercher une voile"
          />

          <GearSearchSelect
            v-model="form.board"
            type="boards"
            label="Planche"
            placeholder="Rechercher une planche"
            empty-hint="Tapez pour rechercher une planche"
          />

          <h5 class="mt-4">Covoiturage</h5>
          <b-form-group label="Places proposees (0 a 10)">
            <b-form-input v-model.number="form.covoit" type="number" min="0" max="10" />
          </b-form-group>

          <b-form-group label="Commentaire">
            <b-form-textarea
              v-model="form.commentaire"
              rows="3"
              placeholder="Ajouter un commentaire"
            />
          </b-form-group>

          <div class="mt-4">
            <b-button type="submit" variant="primary" :disabled="updateLoading">
              <b-spinner v-if="updateLoading" small class="mr-2" />
              Mettre a jour
            </b-button>
            <b-button
              variant="danger"
              class="ml-2"
              :disabled="deleteLoading"
              @click="deleteRegistration"
            >
              <b-spinner v-if="deleteLoading" small class="mr-2" />
              Se desinscrire
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>

    <div v-else class="text-center p-5">
      <b-spinner label="Chargement..." />
    </div>
  </div>
</template>

<script>
import RegistrationService from "@/services/registration.service";
import OpeningService from "@/services/opening.service";
import GearSearchSelect from "@/components/GearSearchSelect.vue";

export default {
  name: "ManageRegistration",
  components: {
    GearSearchSelect
  },
  data() {
    return {
      registration: {},
      participant: {},
      form: {
        harnais: false,
        combi: false,
        sail: null,
        board: null,
        covoit: 0,
        commentaire: ""
      },
      registrationLoaded: false,
      updateLoading: false,
      deleteLoading: false,
      error: null
    };
  },
  mounted() {
    this.fetchRegistration();
  },
  methods: {
    getInscriptionId() {
      return (
        this.$route.params.inscriptionId ||
        this.$route.params.id ||
        this.$route.params.token
      );
    },
    formatDate(date) {
      if (!date) return "Date a confirmer";
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return "Date a confirmer";
      return parsed.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long"
      });
    },
    normalizeGearValue(value) {
      if (!value) return null;
      if (typeof value === "string") {
        return { _id: value };
      }
      const id = value.id || value._id || null;
      if (!id) return null;
      return { _id: id };
    },
    normalizeRegistrationPayload(payload) {
      const source = payload && typeof payload === "object" ? payload : {};
      const registrationSource =
        source.registration ||
        source.inscription ||
        source.opening ||
        source.ouverture ||
        source;

      const participant =
        source.participantInfos ||
        source.participantInfo ||
        source.participant ||
        registrationSource.participantInfos ||
        registrationSource.participantInfo ||
        registrationSource.participant ||
        {};

      const registration = {
        id:
          registrationSource.id ||
          registrationSource._id ||
          source.id ||
          source._id ||
          this.getInscriptionId(),
        title: registrationSource.title || source.title || "",
        date: registrationSource.date || source.date || null,
        owners: registrationSource.owners || source.owners || [],
        description: registrationSource.description || source.description || ""
      };

      return {
        registration,
        participant
      };
    },
    buildFormFromParticipant(participant) {
      const gear = participant && participant.gear && typeof participant.gear === "object"
        ? participant.gear
        : participant || {};
      const covoitRaw =
        participant && Object.prototype.hasOwnProperty.call(participant, "covoit")
          ? participant.covoit
          : participant &&
            participant.carpool &&
            Object.prototype.hasOwnProperty.call(participant.carpool, "seatsAvailable")
            ? participant.carpool.seatsAvailable
            : 0;
      const covoitValue = Number(covoitRaw);

      this.form = {
        harnais: gear.harnais === true || gear.harness === true,
        combi: gear.combi === true || gear.wetsuit === true,
        sail: gear.sail || participant.sail || null,
        board: gear.board || participant.board || null,
        covoit: Number.isFinite(covoitValue) ? Math.min(Math.max(covoitValue, 0), 10) : 0,
        commentaire:
          (participant && (participant.commentaire || participant.comment || participant.notes)) || ""
      };
    },
    async fetchRegistration() {
      const inscriptionId = this.getInscriptionId();
      this.error = null;
      this.registrationLoaded = false;

      try {
        const res = await RegistrationService.getByToken(inscriptionId);
        const data = res && res.data ? res.data : {};
        const normalized = this.normalizeRegistrationPayload(data);
        this.registration = normalized.registration;
        this.participant = normalized.participant;
        this.buildFormFromParticipant(this.participant);
      } catch (err) {
        this.error = err.message || "Erreur de chargement de l'inscription.";
      } finally {
        this.registrationLoaded = true;
      }
    },
    buildUpdatePayload() {
      return {
        harnais: !!this.form.harnais,
        combi: !!this.form.combi,
        sail: this.normalizeGearValue(this.form.sail),
        board: this.normalizeGearValue(this.form.board),
        covoit: Number.isFinite(Number(this.form.covoit))
          ? Math.min(Math.max(Number(this.form.covoit), 0), 10)
          : 0,
        commentaire: this.form.commentaire || ""
      };
    },
    async submitUpdate() {
      const inscriptionId = this.getInscriptionId();
      const payload = this.buildUpdatePayload();

      this.updateLoading = true;
      this.error = null;
      try {
        await RegistrationService.updateByToken(inscriptionId, payload);
        this.$bvToast.toast("Inscription mise a jour.", {
          title: "Succes",
          variant: "success",
          solid: true
        });
        await this.fetchRegistration();
      } catch (err) {
        const openingId = this.registration && this.registration.id;
        const participantId = this.participant && (this.participant._id || this.participant.id);
        if (openingId && participantId) {
          try {
            await OpeningService.updateParticipant(openingId, participantId, payload);
            this.$bvToast.toast("Inscription mise a jour.", {
              title: "Succes",
              variant: "success",
              solid: true
            });
            await this.fetchRegistration();
            return;
          } catch (fallbackError) {
            this.error = fallbackError.message || "Erreur de mise a jour.";
          }
        } else {
          this.error = err.message || "Erreur de mise a jour.";
        }
      } finally {
        this.updateLoading = false;
      }
    },
    async deleteRegistration() {
      const inscriptionId = this.getInscriptionId();
      if (!confirm("Supprimer votre inscription ?")) return;
      this.deleteLoading = true;
      this.error = null;
      try {
        await RegistrationService.removeByToken(inscriptionId);
        this.$bvToast.toast("Inscription supprimee.", {
          title: "Succes",
          variant: "success",
          solid: true
        });
        this.$router.push("/mes-inscriptions");
      } catch (err) {
        this.error = err.message || "Erreur lors de la suppression.";
      } finally {
        this.deleteLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 800px;
}
</style>
